import { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import { COLORS as C, RADIUS, MONO } from '../constants/ui';

const CLOSED_ISSUE_STATUSES = ['Fixed', 'Not Reproducible', 'Invalid', "Won't Fix", 'Duplicate', 'Closed'];

const dayKey = (d) => new Date(d).toISOString().slice(0, 10);

/**
 * Ideal-vs-actual burndown for the selected sprint.
 *
 * Everything is derived from issues the board already loaded — no extra
 * request. Scope is measured in story points when the sprint uses them and
 * falls back to a straight issue count when it doesn't, which is common here;
 * a points-based chart would otherwise render as a flat zero line and read as
 * "nothing to do" rather than "points aren't being tracked".
 */
export default function SprintBurndown({ sprint, issues }) {
  const [open, setOpen] = useState(false);

  const model = useMemo(() => {
    if (!sprint?.start_date || !sprint?.end_date) return null;
    const rows = issues || [];
    if (!rows.length) return null;

    const start = new Date(dayKey(sprint.start_date));
    const end = new Date(dayKey(sprint.end_date));
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return null;

    const totalPoints = rows.reduce((n, i) => n + (Number(i.story_points) || 0), 0);
    const usePoints = totalPoints > 0;
    const weightOf = (i) => (usePoints ? Number(i.story_points) || 0 : 1);
    const scope = usePoints ? totalPoints : rows.length;

    // How much closed on each day, so the actual line can step down.
    const burnedOn = new Map();
    for (const i of rows) {
      if (!CLOSED_ISSUE_STATUSES.includes(i.status) || !i.closeddate) continue;
      const k = dayKey(i.closeddate);
      burnedOn.set(k, (burnedOn.get(k) || 0) + weightOf(i));
    }

    const days = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      days.push(dayKey(d));
    }
    if (days.length < 2) return null;

    const todayKey = dayKey(new Date());
    let remaining = scope;
    const step = scope / (days.length - 1);

    const data = days.map((k, idx) => {
      remaining -= burnedOn.get(k) || 0;
      return {
        day: k.slice(5), // MM-DD — the year is never in question inside a sprint
        ideal: Math.max(0, Number((scope - step * idx).toFixed(2))),
        // Stop drawing the actual line past today, otherwise a mid-sprint
        // chart shows a flat run to the end that looks like stalled work.
        actual: k <= todayKey ? Math.max(0, remaining) : null,
      };
    });

    const closedWeight = rows
      .filter((i) => CLOSED_ISSUE_STATUSES.includes(i.status))
      .reduce((n, i) => n + weightOf(i), 0);

    return { data, scope, usePoints, remaining: Math.max(0, scope - closedWeight) };
  }, [sprint, issues]);

  if (!model) return null;

  const unit = model.usePoints ? 'points' : 'issues';

  return (
    <div
      style={{
        background: '#fff',
        border: `1px solid ${C.line}`,
        borderRadius: RADIUS.card,
        marginBottom: '1rem',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '0.6rem 0.9rem',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span
          style={{
            fontFamily: MONO,
            fontSize: '0.62rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: C.inkFaint,
          }}
        >
          Burndown
        </span>
        <span style={{ fontSize: '0.78rem', color: C.inkSoft }}>
          <strong style={{ color: C.ink }}>{model.remaining}</strong> of {model.scope} {unit} left
        </span>
        <span style={{ marginLeft: 'auto', color: C.inkFaint, fontSize: '0.72rem' }}>
          {open ? 'Hide ▴' : 'Show ▾'}
        </span>
      </button>

      {open && (
        <div style={{ height: 220, padding: '0 0.9rem 0.9rem' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={model.data} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.sunken} />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: C.inkFaint }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: C.inkFaint }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: '0.78rem', borderRadius: 8, border: `1px solid ${C.line}` }}
                formatter={(v, name) => [v, name === 'ideal' ? 'Ideal' : 'Actual']}
              />
              <Legend wrapperStyle={{ fontSize: '0.72rem' }} formatter={(v) => (v === 'ideal' ? 'Ideal' : 'Actual')} />
              <Line
                type="monotone"
                dataKey="ideal"
                stroke={C.inkFaint}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="actual"
                stroke={C.accent}
                strokeWidth={3}
                dot={{ r: 2 }}
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
